import React, { useState, useEffect } from "react";
import { Table, Container, Collapse, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye, faTimes, faChevronDown, faChevronUp, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { useAppContext } from "../../../context/appContext";
import { isViewOnly } from "../../../utils/permissions";
import { Paginate } from "../../../components";
import TooltipInfo from "../../../components/TooltipInfo";
import { EditProjectGroupModal, DeleteProjectGroupModal } from "./UpdateProjectGroup";

const ProjectGroupsListPage = ({
  env,
  handleEnvChange,
  groups,
  isLoading,
  isError,
  pageResetFlag,
  assetTypes,
  countries,
  currentTenant
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProjectGroup, setSelectedProjectGroup] = useState(null);
  const [selectedAssetTypeId, setSelectedAssetTypeId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  const { user } = useAppContext();
  const viewOnly = isViewOnly(user);

  const itemsPerPage = 10;
  const indexOfLastGroup = currentPage * itemsPerPage;
  const indexOfFirstGroup = indexOfLastGroup - itemsPerPage;
  const currentProjectGroups = groups?.slice(indexOfFirstGroup, indexOfLastGroup);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const totalPages = groups?.length ? Math.ceil(groups?.length / itemsPerPage) : 0;

  const getAssetTypeId = (assetTypes, name, segment) => {
    const matchingAsset = assetTypes.find((asset) => asset.name === name && asset.segment === segment);
    return matchingAsset ? matchingAsset.id : null;
  };

  const handleEditClick = (projectGroup) => {
    setSelectedProjectGroup(projectGroup);
    setSelectedAssetTypeId(getAssetTypeId(assetTypes, projectGroup.assetType, projectGroup.segment));
    setShowEditModal(true);
  };

  const handleDeleteClick = (projectGroup) => {
    setSelectedProjectGroup(projectGroup);
    setShowDeleteModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedProjectGroup(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedProjectGroup(null);
  };

  const toggleExpand = (id) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [pageResetFlag, groups]);

  // accessing localStorage instead of ContextAPI because of how the new client is initialized per environment
  const currentTenantName = localStorage.getItem("selectedTenantName") || null;

  // handle localStorage issues with undefined/null for older browsers
  const isTenantSelected =
    currentTenantName !== null &&
    String(currentTenantName) !== "undefined" &&
    String(currentTenantName) !== "null" &&
    currentTenantName !== "";

  const tenantHasGroups = isTenantSelected && groups?.length >= 1;

  if (isLoading || isError || !isTenantSelected) {
    return isLoading ? (
      <Container>
        <p>Loading project groups...</p>
      </Container>
    ) : isError ? (
      <Container>
        <p>Error loading project groups.</p>
      </Container>
    ) : (
      <Container>
        <h5>Choose a tenant to view project groups.</h5>
      </Container>
    );
  }

  return (
    <>
      {tenantHasGroups ? (
        <Container fluid>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Location</th>
                <th scope="col">Operator</th>
                <th scope="col">Segment</th>
                <th scope="col">Asset Type</th>
                <th scope="col">Public ID</th>
                <th scope="col">Tenant</th>
                <th scope="col">UUID</th>
                <th scope="col" style={{ width: "80px" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {currentProjectGroups?.map((projectGroup) => (
                <React.Fragment key={projectGroup.id}>
                  <tr
                    onClick={() => toggleExpand(projectGroup.id)}
                    style={{ cursor: "pointer" }}
                    className={expandedGroups[projectGroup.id] ? "table-active" : ""}
                  >
                    <td>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <span>{projectGroup.name}</span>
                        <FontAwesomeIcon icon={expandedGroups[projectGroup.id] ? faChevronUp : faChevronDown} size="sm" />
                      </div>
                    </td>
                    <td>{projectGroup.location}</td>
                    <td>{projectGroup.operator}</td>
                    <td>{projectGroup.segment}</td>
                    <td>{projectGroup.assetType}</td>
                    <td>{projectGroup.publicId}</td>
                    <td>{currentTenantName}</td>
                    <td>{projectGroup.id}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="d-flex justify-content-around align-items-center" style={{ minWidth: "80px" }}>
                        {viewOnly ? (
                          <FontAwesomeIcon icon={faEye} style={{ cursor: "pointer" }} onClick={() => handleEditClick(projectGroup)} />
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon={faEdit}
                              style={{ cursor: "pointer", marginRight: "10px" }}
                              onClick={() => handleEditClick(projectGroup)}
                            />
                            <FontAwesomeIcon
                              icon={faTimes}
                              style={{ cursor: "pointer", color: "red" }}
                              onClick={() => handleDeleteClick(projectGroup)}
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="8" className="p-0">
                      <Collapse in={expandedGroups[projectGroup.id]}>
                        <div className="p-3 bg-light w-100">
                          <h6 className="mb-3">
                            Sites ({projectGroup.sites?.length || 0})
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip id="username-tooltip" className="text-start">
                                  You can add sites (projects) to groups in Catalyst.
                                </Tooltip>
                              }
                            >
                              <span style={{ cursor: "pointer", marginLeft: "5px" }}>
                                <FontAwesomeIcon icon={faInfoCircle} className="text-primary" style={{ fontSize: "16px" }} />
                              </span>
                            </OverlayTrigger>
                          </h6>
                          {projectGroup.sites?.length > 0 ? (
                            <Table size="sm" bordered className="w-100">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Country</th>
                                  <th>Description</th>
                                  <th>Start Year</th>
                                  <th>End Year</th>
                                  <th>UUID</th>
                                </tr>
                              </thead>
                              <tbody>
                                {projectGroup.sites.map((site) => (
                                  <tr key={site.id}>
                                    <td>{site.name}</td>
                                    <td>{site.country}</td>
                                    <td>{site.description || "—"}</td>
                                    <td>{site.groupStartYear}</td>
                                    <td>{site.groupEndYear}</td>
                                    <td>{site.id}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <p className="text-muted">No sites available for this project group.</p>
                          )}

                          <div className="mt-3">
                            <strong>Coordinates:</strong> {projectGroup.coordinates ? projectGroup.coordinates.join(", ") : ""}
                            <br />
                            <strong>OGMP Versions:</strong>{" "}
                            {projectGroup.ogmpVersions?.length > 0 ? projectGroup.ogmpVersions.join(", ") : "None"}
                          </div>
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </Table>

          <Paginate currentPage={currentPage} totalPages={totalPages} handlePageChange={handlePageChange} />

          {/* modals */}
          {selectedProjectGroup && (
            <>
              <EditProjectGroupModal
                handleEnvChange={handleEnvChange}
                show={showEditModal}
                projectGroup={selectedProjectGroup}
                assetTypeId={selectedAssetTypeId}
                handleClose={handleCloseModal}
                tenantName={currentTenantName}
                tenantId={currentTenant}
                env={env}
                viewOnly={viewOnly}
                assetTypes={assetTypes}
                countries={countries}
              />

              <DeleteProjectGroupModal
                show={showDeleteModal}
                projectGroup={selectedProjectGroup}
                handleClose={handleCloseDeleteModal}
                handleEnvChange={handleEnvChange}
                env={env}
                tenantName={currentTenantName}
                tenantId={currentTenant}
              />
            </>
          )}
        </Container>
      ) : (
        <h5>
          <TooltipInfo message="Chosen Tenant does not have a project group." tooltipText="You can start by adding group to tenant" />
        </h5>
      )}
    </>
  );
};

export default ProjectGroupsListPage;
